import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'next-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Trans } from 'react-i18next';
import { UserHasPermission } from 'utils/auth/roles-and-permissions';

import {
  useAddWarehouse,
  useEditWarehouse,
  useGetWarehouseCategories,
  useGetWarehouseSpecialists,
} from '@/modules/settings/property/apiHooks';
import { usePropertySettings } from '@/modules/settings/property/components/logic';
import { IPropertySettings } from '@/modules/settings/property/types';
import AddEditTeamMember from '@/modules/settings/Team/components/AddEditTeamMember';
import {
  Button,
  InputField,
  SelectField,
  Typography,
} from '@/shared/components/atoms';
import TippyTooltip from '@/shared/components/atoms/TippyTooltip';
import TooltipLite from '@/shared/components/atoms/TooltipLite';
import { AssetItemProps } from '@/shared/components/molecules/ChooseAsset/ChooseAsset';
import { permissions } from '@/shared/constant/roles-and-permissions';
import { useAuth } from '@/shared/hooks/context/auth';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import InfoIcon from '~/images/shared/ExleminationMark.svg';

const AddEditWarehouse: React.FC<{
  property: AssetItemProps | null;
  disabledCategories?: Set<number>;
  warehouseId?: number;
  defaultValues?: Omit<IPropertySettings.IAddWarehouseSchema, 'location'>;
  onSuccess?: () => void;
}> = ({
  property,
  onSuccess,
  warehouseId,
  defaultValues,
  disabledCategories,
}) => {
  const { t } = useTranslation();

  const { user } = useAuth();

  const { openModal, closeModal } = useModal();

  const { useAddEditWarehouse } = usePropertySettings();

  const { validationSchema, getCategoriesList, getWarehouseSpecialistsList } =
    useAddEditWarehouse();

  const formMethods = useForm<IPropertySettings.IAddWarehouseSchema>({
    resolver: yupResolver(validationSchema),
    defaultValues: { ...defaultValues, location: property?.id },
    mode: 'onSubmit',
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = formMethods;

  const { useGetCountryID } = usePropertySettings();
  const countryId = useGetCountryID(property ? property?.country : undefined);
  const { data: categories, isLoading: isCategoriesLoading } =
    useGetWarehouseCategories(countryId);

  const {
    data: warehouseSpecialists,
    isLoading: isWarehouseSpecialistsLoading,
    refetch: refetchWarehouseSpecialists,
  } = useGetWarehouseSpecialists(property?.id);

  const { mutate: create, isLoading: isAdding } = useAddWarehouse();
  const { mutate: edit, isLoading: isEditing } = useEditWarehouse();
  const isMutating = isAdding || isEditing;

  const handleSuccess = () => {
    closeModal();
    toast.success(t(`${warehouseId ? 'edit' : 'add'}-warehouse-success`));
    if (!warehouseId && onSuccess) onSuccess();
  };

  const handleTeamMemberAddedSuccess = () => {
    closeModal();
    toast.success(t(`warehouse-team-member-added`));
    refetchWarehouseSpecialists();
  };

  const onSubmit: SubmitHandler<IPropertySettings.IAddWarehouseSchema> = (
    data
  ) => {
    if (warehouseId)
      edit(
        { ...data, warehouseId, entityId: property?.id },
        { onSuccess: handleSuccess }
      );
    else create(data, { onSuccess: handleSuccess });
  };

  const hasPermissionToAddTeamMember = UserHasPermission({
    action: permissions.CAN_ADD_TEAM_MEMBER_TO_WAREHOUSE,
    role: user?.userGroupId,
  });

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className='m-auto flex h-full w-full max-w-[100%] flex-col justify-between'
      >
        <div className='m-auto flex h-full w-full flex-col justify-between tablet:max-w-[75%] desktop:max-w-[50%]'>
          <div>
            <Typography variant='h1' weight='semiBold' className='mt-24'>
              {t(`${warehouseId ? 'edit' : 'add'}-warehouse`)}
            </Typography>
            <Typography
              variant='subtitle2'
              className=' mb-40 text-surface-10'
              weight='bold'
            >{`${t('property')}: ${property?.name}`}</Typography>
            <div className='flex flex-col gap-16'>
              <InputField
                borderColor='border-neutralVariant-90'
                width='w-full'
                disabled={isMutating}
                label={t('warehouse-name')}
                placeholder={t('warehouse-name')}
                error={!!errors.name}
                errorMessage={errors.name?.message}
                {...register('name')}
              />
              <SelectField
                isMulti
                width='w-full'
                borderColor='#DFE3E8'
                isLoading={isCategoriesLoading && !!property?.id}
                id='categories'
                py='0.275rem'
                isDisabled={isMutating}
                label={t('stock-type')}
                placeholder={t('stock-type')}
                list={getCategoriesList(categories) || []}
                customMultiValueContainer={(children, option) =>
                  disabledCategories?.has(option.value) ? (
                    <TooltipLite content={t('cannot-delete-category')}>
                      <div className='pointer-events-none opacity-5'>
                        {children}
                      </div>
                    </TooltipLite>
                  ) : (
                    children
                  )
                }
                getLabel={({ label }: { label: string }) => label}
                getValue={({ value }: { value: number }) => value}
              />
              <SelectField
                isMulti
                width='w-full'
                borderColor='#DFE3E8'
                isLoading={isWarehouseSpecialistsLoading && !!property?.id}
                id='responsible'
                py='0.275rem'
                isDisabled={isMutating}
                label={
                  <div className='flex items-center gap-4'>
                    {t('authorized-team-members')}
                    <TippyTooltip
                      allowHTML
                      maxWidth='265px'
                      content={
                        <Trans
                          i18nKey='warehouse-authorized-team-member-info'
                          components={{
                            strong: <strong className='text-surface-5' />,
                          }}
                        />
                      }
                    >
                      <span className='cursor-pointer text-surface-10'>
                        <InfoIcon width='16' height='16' />
                      </span>
                    </TippyTooltip>
                  </div>
                }
                placeholder={t('authorized-team-members')}
                list={getWarehouseSpecialistsList(warehouseSpecialists) || []}
                getLabel={({ label }: { label: string }) => label}
                getValue={({ value }: { value: number }) => value}
                createOption={
                  hasPermissionToAddTeamMember
                    ? {
                        title: t('add-team-member-to-warehouse'),
                        subTitle: t('add-team-member-to-warehouse-sub-title'),
                        onClick: () => {
                          openModal(
                            <AddEditTeamMember
                              onSuccess={handleTeamMemberAddedSuccess}
                              formName='create a warehouse - add team member from select field'
                            />,
                            {
                              variant: 'full',
                              title: t('add-team-member-to-warehouse'),
                            }
                          );
                        },
                      }
                    : undefined
                }
              />
            </div>
          </div>
        </div>
        <div className='flex w-full max-w-[100%] flex-col'>
          <Button
            size='lg'
            className='h-56 w-full self-end !rounded-xl'
            loading={isMutating}
            disabled={!isValid}
            type='submit'
          >
            {t(warehouseId ? 'save-edits' : 'confirm')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default AddEditWarehouse;
